import { useEffect, useMemo, useState } from "react";
import { Autocomplete, AutocompleteItem, Chip } from "@heroui/react";
import MainStore from "../MainStore";
import { HeroIcon } from "../HeroIcon";

const mainstore = MainStore();

const defaultPrimitiveTypes = [
  "entity",
  "evidence",
  "result",
  "category",
  "query",
  "activity",
  "marketsegment",
  "detail",
  "summary",
];

function normalizeCategories(categories = []) {
  return categories
    .map((category) => (category ? category : undefined))
    .filter((category) => category);
}

function ensureArray(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (value === undefined || value === null || value === "") {
    return [];
  }
  return [value];
}

function normalizeTypeFilter(value) {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === "string" ? entry.trim() : entry))
      .filter((entry) => entry !== undefined && entry !== null && entry !== "");
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry);
  }
  if (value === undefined || value === null || value === "") {
    return [];
  }
  return [value];
}

function resolvePrimitiveTypeFilters({ types, item }) {
  const sources = [
    types,
    item?.primitiveTypes,
    item?.primitiveType,
    item?.allowedPrimitiveTypes,
    item?.allowedPrimitiveType,
    item?.categoryTypes,
    item?.categoryType,
    item?.types,
  ]
    .map((entry) => normalizeTypeFilter(entry))
    .filter((entry) => entry.length > 0);

  if (sources.length === 0) {
    return undefined;
  }

  let merged = [...new Set(sources[0])];
  for (let idx = 1; idx < sources.length; idx += 1) {
    const allowed = new Set(sources[idx]);
    merged = merged.filter((entry) => allowed.has(entry));
  }

  return merged;
}

function getCategoryFromId(id) {
  if (id === undefined || id === null) {
    return undefined;
  }
  return mainstore.category(id);
}

function buildOptionKey(option, index) {
  if (option.key !== undefined && option.key !== null) {
    return String(option.key);
  }
  if (option.categoryId !== undefined && option.categoryId !== null) {
    return String(option.categoryId);
  }
  return `option-${index}`;
}

export default function CategoryIdSelector({
  item,
  primitive,
  primitiveList,
  allowNone,
  activeOnly,
  local,
  callback,
  types,
  referenceIds,
  localCategoryId,
  filterForExtractor,
  disabled,
  locked,
  selectionMode,
  selectedValues,
  availableCategories,
  className,
  onSelectionChange,
  showCount = true,
  ...props
}) {
  const resolvedSelectionMode = (selectionMode || item?.selectionMode || (Array.isArray(selectedValues || item?.value) ? "multiple" : "single"));
  const isMultiple = resolvedSelectionMode === "multiple";
  const [internalSelection, setInternalSelection] = useState([]);
  const primitiveTypeFilters = useMemo(
    () => resolvePrimitiveTypeFilters({ types, item }),
    [item, types]
  );

  const computed = useMemo(() => {
    const list = [];

    if (allowNone || item?.allowNone) {
      list.push({
        key: "none",
        title: "No items",
        categoryId: undefined,
        category: undefined,
      });
    }

    let available = normalizeCategories(
      availableCategories
        ? availableCategories
        : item?.referenceIds
        ? item.referenceIds.map((id) => getCategoryFromId(id))
        : mainstore.categories()
    );

    let itemCountByType;

    if (activeOnly || item?.activeOnly) {
      if (primitive || primitiveList) {
        const sourceList = (primitiveList ?? [primitive])
          .map((entry) => {
            if (!entry) {
              return [];
            }
            if (entry.type === "category") {
              return entry.origin;
            }
            if (entry.type === "query") {
              return entry.primitives.imports.allItems;
            }
            return entry;
          })
          .flat();

        const uniqueItems = mainstore.uniquePrimitives(sourceList);
        const itemsForProcessing = uniqueItems.map((entry) => entry.itemsForProcessing).flat();
        let lookups = mainstore.uniquePrimitives(
          [itemsForProcessing, itemsForProcessing.map((entry) => entry.primitives.directDescendants)].flat(Infinity)
        );

        if (lookups.length > 0) {
          lookups = [...uniqueItems, ...lookups];
          itemCountByType = {};
          for (const entry of lookups) {
            if (entry.referenceId !== undefined && entry.referenceId !== null) {
              itemCountByType[entry.referenceId] = (itemCountByType[entry.referenceId] ?? 0) + 1;
            }
          }
          const ids = lookups
            .map((entry) => entry.referenceId)
            .flat()
            .filter((id, idx, arr) => id && arr.indexOf(id) === idx);
          available = available.filter((category) => ids.includes(category.id));
        }
      }
    }

    if (filterForExtractor || item?.filterForExtractor) {
      available = available.filter((category) => category?.ai?.extract);
    }

    const callerProvidedCategories = Array.isArray(availableCategories);

    for (const category of available) {
      if (
        callerProvidedCategories ||
        item?.referenceIds ||
        defaultPrimitiveTypes.includes(category.primitiveType)
      ) {
        list.push({
          title: category.title,
          count: itemCountByType ? itemCountByType[category.id] : undefined,
          categoryId: category.id,
          category,
          icon: category.icon,
        });
      }
    }

    let filteredList = list;

    if (primitiveTypeFilters?.length) {
      filteredList = filteredList.filter(
        (entry) =>
          !entry.category ||
          primitiveTypeFilters.includes(entry.category.primitiveType)
      );
    }

    if (referenceIds) {
      filteredList = filteredList.filter((entry) => referenceIds.includes(entry.category?.id));
    }

    return {
      options: filteredList.map((entry, index) => ({
        ...entry,
        key: buildOptionKey(entry, index),
      })),
      itemCountByType,
    };
  }, [
    allowNone,
    activeOnly,
    availableCategories,
    filterForExtractor,
    item,
    primitive,
    primitiveList,
    referenceIds,
    primitiveTypeFilters,
  ]);

  const optionLookup = useMemo(() => {
    const map = new Map();
    computed.options.forEach((option) => {
      map.set(String(option.key), option);
    });
    return map;
  }, [computed.options]);

  const optionByCategoryId = useMemo(() => {
    const map = new Map();
    computed.options.forEach((option) => {
      if (option.categoryId !== undefined && option.categoryId !== null) {
        map.set(option.categoryId, option);
      }
    });
    return map;
  }, [computed.options]);

  const resolvedValue = selectedValues ?? item?.value ?? internalSelection;

  const normalizedMultiSelection = useMemo(() => {
    if (!isMultiple) {
      return [];
    }
    return ensureArray(resolvedValue)
      .map((entry) => {
        if (entry === undefined || entry === null || entry === "") {
          return undefined;
        }
        if (typeof entry === "number") {
          return entry;
        }
        const parsed = parseInt(entry, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      })
      .filter((entry) => entry !== undefined);
  }, [isMultiple, resolvedValue]);

  const singleSelectedValue = useMemo(() => {
    if (isMultiple) {
      return undefined;
    }
    if (resolvedValue === undefined || resolvedValue === null || resolvedValue === "") {
      return undefined;
    }
    if (typeof resolvedValue === "number") {
      return resolvedValue;
    }
    const parsed = parseInt(resolvedValue, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }, [isMultiple, resolvedValue]);

  const selectedSet = useMemo(() => new Set(normalizedMultiSelection), [normalizedMultiSelection]);

  const availableOptions = useMemo(() => {
    if (!isMultiple) {
      return computed.options;
    }
    return computed.options.filter((option) => {
      if (option.categoryId === undefined || option.categoryId === null) {
        return true;
      }
      return !selectedSet.has(option.categoryId);
    });
  }, [computed.options, isMultiple, selectedSet]);

  const [inputValue, setInputValue] = useState("");

  const visibleOptions = useMemo(() => {
    const query = inputValue.trim().toLowerCase();
    if (!query) {
      return availableOptions;
    }
    return availableOptions.filter((option) =>
      option.title?.toLowerCase().includes(query)
    );
  }, [availableOptions, inputValue]);

  useEffect(() => {
    if (!isMultiple) {
      if (singleSelectedValue === undefined) {
        setInputValue("");
      } else {
        const option = optionByCategoryId.get(singleSelectedValue);
        setInputValue(option?.title ?? "");
      }
    } else {
      setInputValue("");
    }
  }, [isMultiple, singleSelectedValue, optionByCategoryId]);

  const emitSelectionChange = (categoryIds) => {
    if (!onSelectionChange) {
      return;
    }
    const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];
    const entries = ids
      .map((id) => {
        if (id === undefined || id === null) {
          return computed.options.find((option) => option.categoryId === undefined);
        }
        return optionByCategoryId.get(id);
      })
      .filter((entry) => entry);
    onSelectionChange(entries);
  };

  const commitSelection = (value) => {
    if (isMultiple) {
      const normalized = (Array.isArray(value) ? value : [])
        .map((entry) => {
          if (typeof entry === "number") {
            return entry;
          }
          if (entry === undefined || entry === null || entry === "") {
            return undefined;
          }
          const parsed = parseInt(entry, 10);
          return Number.isNaN(parsed) ? undefined : parsed;
        })
        .filter((entry) => entry !== undefined);
      emitSelectionChange(normalized);
      if (callback) {
        callback(normalized);
      } else if (primitive && item?.key) {
        primitive.setParameter(item.key, normalized);
      } else {
        setInternalSelection(normalized);
      }
    } else {
      const parsed =
        value === undefined || value === null || value === ""
          ? undefined
          : typeof value === "number"
          ? value
          : parseInt(value, 10);
      if (parsed === undefined || Number.isNaN(parsed)) {
        emitSelectionChange([]);
        if (callback) {
          callback(undefined);
        } else if (primitive && item?.key) {
          primitive.setParameter(item.key, undefined);
        } else {
          setInternalSelection(undefined);
        }
        return;
      }
      emitSelectionChange([parsed]);
      if (callback) {
        callback(parsed);
      } else if (primitive && item?.key) {
        primitive.setParameter(item.key, parsed);
      } else {
        setInternalSelection(parsed);
      }
    }
  };

  const handleAddCategory = (categoryId) => {
    if (isMultiple) {
      if (categoryId === undefined || categoryId === null) {
        commitSelection([]);
        return;
      }
      const numericId = typeof categoryId === "number" ? categoryId : parseInt(categoryId, 10);
      if (Number.isNaN(numericId) || selectedSet.has(numericId)) {
        setInputValue("");
        return;
      }
      commitSelection([...normalizedMultiSelection, numericId]);
      setInputValue("");
    } else {
      if (categoryId === undefined || categoryId === null) {
        if (allowNone || item?.allowNone) {
          commitSelection(undefined);
        }
        return;
      }
      const numericId = typeof categoryId === "number" ? categoryId : parseInt(categoryId, 10);
      if (Number.isNaN(numericId)) {
        return;
      }
      commitSelection(numericId);
    }
  };

  const handleChipRemove = (categoryId) => {
    if (!isMultiple) {
      commitSelection(undefined);
      return;
    }
    commitSelection(normalizedMultiSelection.filter((id) => id !== categoryId));
  };

  const handleAutocompleteSelection = (key) => {
    if (!key) {
      return;
    }
    const option = optionLookup.get(String(key));
    if (!option) {
      return;
    }
    handleAddCategory(option.categoryId);
  };

  const selectedChips = isMultiple
    ? normalizedMultiSelection.map((id) => {
        const option = optionByCategoryId.get(id);
        return {
          id,
          label: option?.title ?? `Category ${id}`,
        };
      })
    : [];

  const singleSelectedKey = useMemo(() => {
    if (isMultiple) {
      return null;
    }
    if (singleSelectedValue === undefined) {
      return null;
    }
    const match = computed.options.find((option) => option.categoryId === singleSelectedValue);
    return match ? String(match.key) : null;
  }, [computed.options, isMultiple, singleSelectedValue]);

  const resolvedContainerClass =
    className ?? (props.compact || props.inline ? "" : "ml-auto w-full");
  const stackClass = resolvedContainerClass
    ? `${resolvedContainerClass} flex flex-col gap-2`
    : "flex flex-col gap-2";

  return (
    <div className={stackClass}>
      <Autocomplete
        className="w-full"
        items={visibleOptions}
        allowsCustomValue={false}
        isDisabled={disabled ?? locked ?? item?.locked}
        label={props.title}
        placeholder={item?.placeholder || "Select category"}
        selectedKey={!isMultiple ? singleSelectedKey ?? undefined : undefined}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onSelectionChange={(key) => handleAutocompleteSelection(key ?? null)}
        isClearable={!isMultiple && (allowNone || item?.allowNone)}
        onClear={() => {
          commitSelection(undefined);
          setInputValue("");
        }}
        listboxProps={{
          emptyContent: availableOptions.length === 0 ? "No categories" : undefined,
        }}
      >
        {(option) => (
          <AutocompleteItem
            key={option.key}
            textValue={option.title}
            startContent={
              option.icon ? (
                <HeroIcon icon={option.icon} className="w-4 h-4 text-default-500" />
              ) : undefined
            }
            endContent={
              showCount && option.count !== undefined && option.count !== null ? (
                <span className="text-tiny text-default-500">{option.count}</span>
              ) : undefined
            }
          >
            {option.title}
          </AutocompleteItem>
        )}
      </Autocomplete>
      {isMultiple && selectedChips.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedChips.map((chip) => (
            <Chip
              key={chip.id}
              variant="flat"
              color="primary"
              size="sm"
              isCloseable
              onClose={() => handleChipRemove(chip.id)}
            >
              {chip.label}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}
